import { useEffect, useRef, useState } from 'react'
import { X, Download, Copy, Check, ExternalLink, PlugZap } from 'lucide-react'
import { FocusTrap } from '@/components/FocusTrap'

const MANIFEST_URL = 'https://reserve.parkeze.com/outlook/manifest.xml'
const OUTLOOK_SIDELOAD_URL = 'https://aka.ms/olksideload'

interface Props {
  onClose: () => void
}

export function OutlookAddinModal({ onClose }: Props) {
  const [copied, setCopied] = useState(false)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose()
  }

  function copyUrl() {
    navigator.clipboard.writeText(MANIFEST_URL).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function downloadManifest() {
    const a = document.createElement('a')
    a.href = MANIFEST_URL
    a.download = 'parkeze-manifest.xml'
    a.click()
  }

  return (
    <div
      ref={overlayRef}
      role="presentation"
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <FocusTrap>
      <div
        className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="outlook-addin-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2.5">
            <PlugZap className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
            <h2 id="outlook-addin-title" className="text-base font-semibold">Add to Outlook</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close Outlook add-in instructions"
            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-5">
          <p className="text-sm text-muted-foreground">
            Sideload the Parkeze Guest Parking add-in into Outlook using one of the methods below.
          </p>

          {/* Option A — Add from URL (Outlook Web) */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Option A — Add from URL <span className="text-xs font-normal text-muted-foreground">(Outlook Web)</span></p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Click the link below to open the Outlook add-in sideload page</li>
              <li>Choose <strong>Add from URL</strong> and paste the manifest link</li>
            </ol>
            <div className="flex items-center gap-2 mt-2">
              <code className="flex-1 text-xs bg-muted px-3 py-2 rounded-md break-all select-all">
                {MANIFEST_URL}
              </code>
              <button
                type="button"
                onClick={copyUrl}
                title="Copy URL"
                className="shrink-0 p-2 rounded-md border border-border text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <a
              href={OUTLOOK_SIDELOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-1"
            >
              Open Outlook sideload page
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div className="border-t border-border" />

          {/* Option B — Add from file (Desktop) */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Option B — Add from file <span className="text-xs font-normal text-muted-foreground">(Outlook Desktop)</span></p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Download the manifest file below</li>
              <li>In Outlook, go to <strong>Get Add-ins → My Add-ins</strong></li>
              <li>Choose <strong>Add a custom add-in → Add from file</strong></li>
              <li>Select the downloaded <code className="text-xs bg-muted px-1 rounded">parkeze-manifest.xml</code></li>
            </ol>
            <button
              type="button"
              onClick={downloadManifest}
              className="mt-2 flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Download className="w-4 h-4" />
              Download Manifest
            </button>
          </div>
        </div>
      </div>
      </FocusTrap>
    </div>
  )
}
